
import { useQuery } from '@apollo/client';
import { useCallback, useRef, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { Radio, Dropdown, Search, Icon, Container, Placeholder } from 'semantic-ui-react'

import { ResponsiveContainer } from '../../../../components/ResponsiveContainer';
import { GET_TAGS as GET_POSSIBLE_TAGS } from '../../../../queries/tags';
import { GET_CLASSES } from '../data/GET_CLASSES';
import { GET_ORGANISATION_PERSON_ID_FORMULA } from '../data/GET_ORGANISATION_PERSON_ID_FORMULA';
import { useBlocks } from '../../../../hooks/useBlocks';

import styles from './AdminClassesView.module.css';

const AdminClassesView = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const urlSearchParams = new URLSearchParams(location.search);
  const { currentBlockFilter, currentBlockSuffix, loading: blocksLoading } = useBlocks();
  const [search, setSearch] = useState(urlSearchParams.get('search') || "");
  const [tagIds, setTagIds] = useState<string[]>(urlSearchParams.get('tagIds') ? urlSearchParams.get('tagIds')!.split(',') : []);
  const [currentBlockOnly, setCurrentBlockOnly] = useState(!urlSearchParams.get('tagIds'));
  const { data: meData } = useQuery(GET_ORGANISATION_PERSON_ID_FORMULA);
  const [shouldShowSearch, setShouldShowSearch] = useState(urlSearchParams.get('search') !== null || urlSearchParams.get('tagIds') !== null);

  const { loading: classesLoading, error: classesError, data: classesData, refetch } = useQuery(GET_CLASSES, {
    skip: blocksLoading || !currentBlockFilter,
    variables: {
      search,
      filters: tagIds && tagIds.length > 0 ? tagIds.join(',') : currentBlockOnly ? currentBlockFilter : ""
    }
  });

  useEffect(() => {
    if (classesData?.classes?.length > 6) setShouldShowSearch(true);
  }, [classesData]);

  const { loading: tagsLoading, error: tagsError, data: tagsData } = useQuery(GET_POSSIBLE_TAGS, {
    variables: { showPeopleTags: false, showCourseTags: true }
  });

  const setAndHoldTagIds = useCallback((ids: string[]) => {
    if (!ids.includes(currentBlockFilter)) setCurrentBlockOnly(false);
    setTagIds(ids);
    navigate({ pathname: location.pathname, search: `?search=${search}&tagIds=${ids.join(',')}` });
  }, [search, navigate, location.pathname, currentBlockFilter]);

  const timeoutRef = useRef<any>();

  const handleSearchChange = useCallback((_: any, data: any) => {
    clearTimeout(timeoutRef.current);
    setSearch(data.value);
    timeoutRef.current = setTimeout(() => {
      navigate({ pathname: location.pathname, search: `?search=${data.value}&tagIds=${tagIds.join(',')}` });
      refetch({ search: data.value, filters: tagIds.length > 0 ? tagIds.join(',') : currentBlockOnly ? currentBlockFilter : "" });
    }, 500);
  }, [tagIds, currentBlockOnly, navigate, location.pathname, refetch, currentBlockFilter]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const searchParam = params.get('search');
    if (!searchParam) {
      setSearch("");
      if (!params.has('tagIds')) {
        if (currentBlockFilter) {
          setTagIds([currentBlockFilter]);
          refetch({ search: "", filters: currentBlockFilter });
        }
      } else {
        const newTagIds = (params.get('tagIds') || "").split(',').filter(Boolean);
        setTagIds(newTagIds);
        refetch({ search: "", filters: newTagIds.length > 0 ? newTagIds.join(',') : currentBlockOnly ? currentBlockFilter : "" });
      }
    } else {
      setSearch(searchParam);
      const newTagIds = (params.get('tagIds') || "").split(',').filter(Boolean);
      setTagIds(newTagIds);
      refetch({ search: searchParam, filters: newTagIds.length > 0 ? newTagIds.join(',') : currentBlockOnly ? currentBlockFilter : "" });
    }
  }, [location.search, refetch, currentBlockOnly, currentBlockFilter]);

  // The class query is skipped until blocks resolve, so the network request only fires
  // after `blocksLoading` flips false. Treat both phases as "still loading" so the list
  // never flashes blank — we show skeleton cards instead.
  const isLoading = blocksLoading || classesLoading;

  const classes = (!classesLoading && !classesError && classesData?.classes) ? classesData.classes : [];

  const myClasses = classes.filter((t: any) => {
    if (!meData) return false;
    return t.overseers?.map((i: any) => i.name).includes(meData.me.name) && t.SKU?.endsWith(currentBlockSuffix ?? '');
  });

  const hasOwnClasses = myClasses.length > 0 && shouldShowSearch && myClasses.length < classes.length;

  const FiltersSkeleton = () => (
    <div className={styles.filters} aria-hidden="true">
      <div className={styles.filterBarSkeleton} />
      <div className={styles.filterBarSkeleton} />
      <div className={styles.filterToggleSkeleton} />
    </div>
  );

  const ClassRowSkeleton = () => (
    <div className={styles.classItemSkeleton} aria-hidden="true">
      <Placeholder fluid>
        <Placeholder.Header>
          <Placeholder.Line length="medium" />
        </Placeholder.Header>
        <Placeholder.Paragraph>
          <Placeholder.Line length="short" />
          <Placeholder.Line length="long" />
        </Placeholder.Paragraph>
      </Placeholder>
    </div>
  );

  const ClassRow = ({ t }: { t: any }) => (
    <div className={styles.classItem} key={t.id} onClick={() => navigate(`/classes/${t.id}`)}>
      <div className={styles.className}>
        {t.name}
        {t.archivedAt && (
          <span style={{ marginLeft: '0.5em', fontSize: '0.75em', color: '#888', border: '1px solid #ccc', borderRadius: '3px', padding: '1px 5px', verticalAlign: 'middle' }}>
            Archived
          </span>
        )}
        {!t.archivedAt && t.isHistoric && (
          <span style={{ marginLeft: '0.5em', fontSize: '0.75em', color: '#6c757d', background: '#e9ecef', borderRadius: '3px', padding: '1px 5px', verticalAlign: 'middle' }}>
            Historic
          </span>
        )}
      </div>
      <div className={styles.classSku}>{t.SKU}</div>
      <div className={styles.classMeta}>
        <Icon name="group" /><b>Students:</b> {t.courseParticipantCount}
        {' · '}
        <Icon name="user" /><b>Teachers:</b> {t.overseers?.map((o: any) => o.name).join(', ')}
      </div>
    </div>
  );

  return (
    <ResponsiveContainer>
      <Container style={{ paddingBottom: '3em' }}>
        <h2 className={styles.pageTitle}>Classes</h2>

        {shouldShowSearch ? (
          <div className={styles.filters}>
            <Search
              loading={classesLoading}
              input={{ fluid: true }}
              size="large"
              placeholder="Search..."
              open={false}
              onSearchChange={handleSearchChange}
              value={search}
            />
            <Dropdown
              placeholder="Tags"
              fluid
              multiple
              value={tagIds}
              onChange={(_, { value }) => {
                setAndHoldTagIds(value as string[]);
                refetch({ search, filters: (value as string[]).length > 0 ? (value as string[]).join(',') : currentBlockOnly ? currentBlockFilter : "" });
              }}
              search
              selection
              options={
                tagsLoading || tagsError ? [] :
                (tagsData?.tags || []).map((p: any) => ({ key: p.id, text: p.name, value: `tagId:${p.id}` }))
              }
            />
            <Radio
              toggle
              label="Current block classes only"
              checked={currentBlockOnly}
              onChange={(_, data: any) => {
                setCurrentBlockOnly(data.checked);
                const filters = data.checked
                  ? [...tagIds, currentBlockFilter]
                  : tagIds.filter((t) => t !== currentBlockFilter);
                setAndHoldTagIds(filters);
                refetch({ search, filters: filters.length > 0 ? filters.join(',') : "" });
              }}
            />
          </div>
        ) : isLoading ? (
          <FiltersSkeleton />
        ) : null}

        {hasOwnClasses && (
          <>
            <div className={styles.sectionDivider}>My classes</div>
            <div className={styles.classList}>
              {myClasses.map((t: any) => <ClassRow key={t.id} t={t} />)}
            </div>
            <div className={styles.sectionDivider}>All classes</div>
          </>
        )}

        <div className={styles.classList}>
          {isLoading
            ? Array.from({ length: 6 }).map((_, i) => <ClassRowSkeleton key={i} />)
            : classes.length > 0
              ? classes.map((t: any) => <ClassRow key={t.id} t={t} />)
              : <div className={styles.emptyState}>No classes found.</div>
          }
        </div>

        <div className={styles.spacer} />
      </Container>
    </ResponsiveContainer>
  );
};

export { AdminClassesView }
